// Écran Campagnes (docs/PLAN-2026-09-22.md, phase 3). Une bannière = une campagne ; une bannière de
// fond permanente = une campagne sans dates, priorité basse. Liste avec onglets d'état, recherche et
// filtre de dates ; fiche en trois onglets Éditer / Scénario / Assigner. Les compteurs de boîtes
// touchées sont un calcul réel avec la règle de résolution du cœur (pickCampaign), jamais une
// estimation. Vue calendrier : bascule prévue dans la structure (`view`), non construite.
import type { Brand, Campaign, Person } from '../../../supabase/functions/_shared/core/index.ts';
import {
  BANNER_DISPLAY_WIDTH,
  BANNER_SOURCE_WIDTH,
  BANNER_WARN_BYTES,
  bannerDisplayHeight,
  CAMPAIGN_PRIORITY,
  type CampaignPriority,
  campaignTargets,
  formatBytes,
  GIF_FIRST_FRAME_NOTE,
  isPermanent,
  pickCampaign,
  PRIORITY_HINTS,
  PRIORITY_LABELS,
  priorityOf,
  resolveBrand,
  SINGLE_CAMPAIGN_RULE,
} from '../../../supabase/functions/_shared/core/index.ts';
import { busy, field, fmtDate, h, lines, mount, toast, toLocalInput } from '../dom.ts';
import { uuid } from '../api.ts';
import { campaignStatus, type CampaignStatus, reload, STATUS_LABEL, type Store } from '../state.ts';
import { openModal } from './modal.ts';
import { assetInput } from './upload.ts';

type Tab = 'all' | CampaignStatus;
const TABS: [Tab, string][] = [['all', 'Toutes'], ['live', 'En cours'], ['upcoming', 'À venir'], ['past', 'Terminées']];

/** Boîtes gérées et actives, avec la charte que chacune résout (adresse principale). */
function mailboxes(store: Store): { person: Person; brand: Brand }[] {
  if (!store.brands.length) return [];
  return store.people
    .filter((p) => p.managed && p.active)
    .map((person) => ({ person, brand: resolveBrand(person, person.primary_email, store.brands) }));
}

/** Instant auquel juger une campagne pour compter ses boîtes : maintenant, ou son début / sa fin si elle n'est pas en cours. */
function judgeAt(c: Campaign, now: Date): Date {
  const st = campaignStatus(c, now);
  if (st === 'upcoming' && c.starts_at) return new Date(c.starts_at);
  if (st === 'past' && c.ends_at) return new Date(new Date(c.ends_at).getTime() - 1);
  return now;
}

/** Nombre de boîtes pour lesquelles CETTE campagne est celle retenue (règle complète, une seule gagnante). */
function reachOf(c: Campaign, store: Store, now: Date): number {
  const at = judgeAt(c, now);
  const pool = c.active ? store.campaigns : [...store.campaigns.filter((x) => x.id !== c.id), { ...c, active: true }];
  return mailboxes(store).filter(({ person, brand }) =>
    pickCampaign(pool, { brandId: brand.id, emails: [person.primary_email], noBanner: person.no_banner }, at)?.id === c.id
  ).length;
}

/** Nombre de boîtes que la campagne cible (avant concurrence) : ce que « Assigner » modifie. */
function targetedCount(c: Campaign, store: Store): number {
  return mailboxes(store).filter(({ person, brand }) => !person.no_banner && campaignTargets(c, brand.id, [person.primary_email])).length;
}

function durationLabel(c: Campaign): string {
  if (isPermanent(c)) return 'Illimitée';
  if (!c.starts_at) return `Jusqu’au ${fmtDate(c.ends_at!)}`;
  if (!c.ends_at) return `Depuis le ${fmtDate(c.starts_at)}`;
  const days = Math.max(1, Math.round((new Date(c.ends_at).getTime() - new Date(c.starts_at).getTime()) / 864e5));
  return `${fmtDate(c.starts_at)} → ${fmtDate(c.ends_at)} (${days} j)`;
}

export function campaignsView(store: Store, refresh: () => void): HTMLElement {
  const root = h('div', { class: 'panel campaigns' });
  const listEl = h('div', { class: 'table-wrap' });
  const tabsEl = h('div', { class: 'ctabs' });
  let tab: Tab = 'all';
  let view: 'list' | 'calendar' = 'list'; // la vue calendrier mensuelle est prévue ici, non construite
  const search = h('input', { type: 'search', placeholder: 'Rechercher une campagne…', class: 'csearch' });
  const from = h('input', { type: 'date', title: 'Actives à partir de cette date' });
  const to = h('input', { type: 'date', title: 'Actives jusqu’à cette date' });
  const now = new Date();

  // ------------------------------------------------------------------ fiche : Éditer / Scénario / Assigner
  const edit = (c: Campaign | null) => {
    const isNew = !c;
    const cur: Campaign = structuredClone(
      c ?? {
        id: uuid(),
        name: '',
        image_url: '',
        image_width: BANNER_SOURCE_WIDTH,
        image_height: 300,
        link_url: null,
        alt: null,
        starts_at: now.toISOString(),
        ends_at: new Date(now.getTime() + 7 * 864e5).toISOString(),
        brand_ids: [],
        include_emails: [],
        exclude_emails: [],
        priority: CAMPAIGN_PRIORITY.medium,
        active: true,
        utm_auto: true,
        updated_at: now.toISOString(),
      },
    );

    // --- Éditer
    const name = h('input', { value: cur.name, placeholder: 'ex. Halloween Badaboum 2026' });
    const thumb = h('div', { class: 'thumb free' });
    const dims = h('small', { class: 'muted' });
    const width = h('input', { type: 'number', min: 1, value: cur.image_width });
    const height = h('input', { type: 'number', min: 1, value: cur.image_height });
    const drawThumb = () => {
      mount(thumb, cur.image_url ? h('img', { src: cur.image_url, alt: '' }) : h('span', { class: 'muted' }, `Visuel : ${BANNER_SOURCE_WIDTH} px de large conseillés, hauteur libre`));
      dims.textContent = cur.image_url
        ? `Source ${cur.image_width}×${cur.image_height} px, affichée ${BANNER_DISPLAY_WIDTH}×${bannerDisplayHeight(cur.image_width, cur.image_height)} px dans la signature.`
        : '';
      width.value = String(cur.image_width);
      height.value = String(cur.image_height);
    };
    const img = assetInput(store.api, 'banners', cur.image_url, (u, el) => {
      cur.image_url = u;
      if (el?.naturalWidth) {
        cur.image_width = el.naturalWidth;
        cur.image_height = el.naturalHeight;
      }
      drawThumb();
    });
    width.addEventListener('input', () => {
      cur.image_width = Number(width.value) || cur.image_width;
      drawThumb();
    });
    height.addEventListener('input', () => {
      cur.image_height = Number(height.value) || cur.image_height;
      drawThumb();
    });
    const alt = h('input', { value: cur.alt ?? '', placeholder: 'ex. Halloween au Badaboum, samedi 31 octobre', required: true });
    const tabEdit = h(
      'div',
      { class: 'grid2' },
      field('Nom', name, 'Nom interne, aussi utilisé pour le paramètre utm_campaign.'),
      field('Texte alternatif', alt, 'Obligatoire. Utilisé par les lecteurs d’écran, et affiché par Outlook quand les images sont bloquées.'),
      h(
        'div',
        { class: 'span2' },
        field('Visuel', img.el, `Dépôt de fichier PNG, JPG ou GIF animé (stocké tel quel : ni recompression, ni conversion, ni redimensionnement). Largeur conseillée ${BANNER_SOURCE_WIDTH} px (affichée ${BANNER_DISPLAY_WIDTH} px, net sur écran Retina), hauteur libre, alerte au-delà de ${formatBytes(BANNER_WARN_BYTES)}. ${GIF_FIRST_FRAME_NOTE}`),
        thumb,
        dims,
      ),
      field('Largeur source (px)', width, 'Lue automatiquement au dépôt ; à corriger seulement si le fichier a été remplacé à la main.'),
      field('Hauteur source (px)', height),
    );

    // --- Scénario
    const link = h('input', { type: 'url', value: cur.link_url ?? '', placeholder: 'https://shotgun.live/…' });
    const utm = h('input', { type: 'checkbox', checked: cur.utm_auto });
    const dated = h('input', { type: 'radio', name: 'sched', checked: !isPermanent(cur) });
    const unlimited = h('input', { type: 'radio', name: 'sched', checked: isPermanent(cur) });
    const start = h('input', { type: 'datetime-local', value: cur.starts_at ? toLocalInput(cur.starts_at) : '' });
    const end = h('input', { type: 'datetime-local', value: cur.ends_at ? toLocalInput(cur.ends_at) : '' });
    const datesBox = h('div', { class: 'grid2 tight dates' }, field('Début', start, 'Vide = déjà commencée.'), field('Fin', end, 'Heure de Paris. Vide = sans fin. La bannière disparaît à cette heure.'));
    const syncSched = () => (datesBox.hidden = unlimited.checked);
    dated.addEventListener('change', syncSched);
    unlimited.addEventListener('change', syncSched);
    syncSched();
    const prioInputs = (['high', 'medium', 'low'] as CampaignPriority[]).map((p) => {
      const r = h('input', { type: 'radio', name: 'prio', value: String(CAMPAIGN_PRIORITY[p]), checked: priorityOf(cur.priority) === p });
      return { r, el: h('label', { class: 'prio' }, r, h('span', null, h('b', null, PRIORITY_LABELS[p]), h('small', { class: 'muted' }, PRIORITY_HINTS[p]))) };
    });
    const tabScenario = h(
      'div',
      { class: 'stack' },
      h('div', { class: 'grid2' }, field('Lien au clic', link, 'Billetterie, page événement… Facultatif : sans lien, la bannière ne se clique pas.'), h('label', { class: 'checks field' }, utm, h('span', null, ' Générer automatiquement les UTM', h('small', { class: 'muted block' }, 'Ajoute utm_source=signature, utm_medium=email et utm_campaign=<nom en minuscules> au lien, à la redirection. Un paramètre déjà présent dans le lien n’est pas doublé.')))),
      h('div', { class: 'field' }, h('span', { class: 'field-label' }, 'Programmation'), h('div', { class: 'checks' }, h('label', null, dated, ' Plage de dates'), h('label', null, unlimited, ' Illimité (bannière permanente)')), datesBox),
      h('div', { class: 'field' }, h('span', { class: 'field-label' }, 'Priorité'), h('div', { class: 'prios' }, prioInputs.map((p) => p.el)), h('p', { class: 'rule' }, SINGLE_CAMPAIGN_RULE, ' ', h('span', { class: 'muted' }, 'À priorité égale, la campagne au début le plus récent gagne, et tout le monde voit la même.'))),
    );

    // --- Assigner
    const boxes = mailboxes(store);
    const countFor = (b: Brand) => boxes.filter((m) => m.brand.id === b.id).length;
    const brandBoxes = store.brands.map((b) => {
      const cb = h('input', { type: 'checkbox', value: b.id, checked: cur.brand_ids.includes(b.id) });
      return { cb, el: h('label', null, cb, ` ${b.name} `, h('span', { class: 'chip small' }, `${countFor(b)} boîte${countFor(b) > 1 ? 's' : ''}`)) };
    });
    const include = h('textarea', { rows: 3, placeholder: 'une adresse par ligne' }, cur.include_emails.join('\n'));
    const exclude = h('textarea', { rows: 3, placeholder: 'une adresse par ligne' }, cur.exclude_emails.join('\n'));
    const active = h('input', { type: 'checkbox', checked: cur.active });
    const reach = h('p', { class: 'muted' });
    const draft = (): Campaign => ({
      ...cur,
      name: name.value.trim(),
      link_url: link.value.trim() || null,
      alt: alt.value.trim() || null,
      image_url: img.input.value.trim(),
      starts_at: unlimited.checked ? null : start.value ? new Date(start.value).toISOString() : null,
      ends_at: unlimited.checked ? null : end.value ? new Date(end.value).toISOString() : null,
      brand_ids: brandBoxes.filter((b) => b.cb.checked).map((b) => b.cb.value),
      include_emails: lines(include.value),
      exclude_emails: lines(exclude.value),
      priority: Number(prioInputs.find((p) => p.r.checked)?.r.value ?? CAMPAIGN_PRIORITY.medium),
      active: active.checked,
      utm_auto: utm.checked,
    });
    const drawReach = () => {
      const d = draft();
      const targeted = targetedCount(d, store);
      const winning = reachOf(d, { ...store, campaigns: [...store.campaigns.filter((x) => x.id !== d.id), d] }, now);
      reach.textContent = `${targeted} boîte${targeted > 1 ? 's' : ''} ciblée${targeted > 1 ? 's' : ''} ; ${winning} verr${winning > 1 ? 'ont' : 'a'} cette bannière ${campaignStatus(d, now) === 'upcoming' ? 'à son début' : 'aujourd’hui'} (les autres ont une campagne plus prioritaire, ou sont des boîtes sans bannière).`;
    };
    const tabAssign = h(
      'div',
      { class: 'stack' },
      h('div', { class: 'field' }, h('span', { class: 'field-label' }, 'Par charte'), h('small', null, 'Aucune cochée = tous les lieux.'), h('div', { class: 'checks col' }, brandBoxes.map((b) => b.el))),
      h('div', { class: 'grid2' }, field('Par personne', include, 'Uniquement ces adresses : remplace le ciblage par charte.'), field('Liste d’exclusion', exclude, 'Ces adresses ne verront jamais cette campagne, quel que soit le reste.')),
      h('label', { class: 'checks' }, active, ' Active'),
      reach,
    );
    [...brandBoxes.map((b) => b.cb), include, exclude, active, dated, unlimited, start, end, ...prioInputs.map((p) => p.r)].forEach((el) => el.addEventListener('change', drawReach));
    drawReach();

    // --- onglets de la fiche
    const panes: [string, HTMLElement][] = [['Éditer', tabEdit], ['Scénario', tabScenario], ['Assigner', tabAssign]];
    const bar = h('div', { class: 'ctabs modal-tabs' });
    let current = 0;
    const showPane = (i: number) => {
      current = i;
      panes.forEach(([, el], j) => (el.hidden = j !== i));
      mount(bar, panes.map(([label], j) => h('button', { type: 'button', class: `ghost${j === i ? ' on' : ''}`, onclick: () => showPane(j) }, label)));
    };
    showPane(0);
    const save = h('button', null, 'Enregistrer');
    const close = openModal(isNew ? 'Nouvelle campagne' : cur.name, h('div', null, bar, ...panes.map(([, el]) => el)), [save]);
    drawThumb();
    save.addEventListener('click', () =>
      busy(save, async () => {
        const next = draft();
        if (!next.name) {
          showPane(0);
          throw new Error('Nom obligatoire');
        }
        if (!/^https:\/\//.test(next.image_url) && !next.image_url.startsWith('blob:')) {
          showPane(0);
          throw new Error('Visuel : déposez un fichier ou saisissez une URL https');
        }
        if (!next.alt) {
          showPane(0);
          throw new Error('Texte alternatif obligatoire : c’est ce que voient les lecteurs d’écran et Outlook quand les images sont bloquées');
        }
        if (next.starts_at && next.ends_at && new Date(next.ends_at) <= new Date(next.starts_at)) {
          showPane(1);
          throw new Error('La fin doit être après le début');
        }
        if (next.link_url && !/^https?:\/\//.test(next.link_url)) {
          showPane(1);
          throw new Error('Lien : URL http(s) attendue');
        }
        await store.api.saveCampaign(next, isNew);
        await reload(store);
        close();
        toast(store.api.demo ? 'Campagne enregistrée (démo)' : `Campagne enregistrée — ${isPermanent(next) ? 'bannière permanente, ' : ''}appliquée à la prochaine poussée`);
        refresh();
      }));
    void current;
  };

  // ------------------------------------------------------------------ liste
  const visible = (): Campaign[] => {
    const q = search.value.trim().toLowerCase();
    const f = from.value ? new Date(from.value).getTime() : null;
    const t = to.value ? new Date(to.value).getTime() + 864e5 : null;
    return store.campaigns.filter((c) => {
      if (tab !== 'all' && campaignStatus(c, now) !== tab) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      // filtre de dates : la campagne est active à un moment de la plage (une permanente l'est toujours)
      const s = c.starts_at ? new Date(c.starts_at).getTime() : -Infinity;
      const e = c.ends_at ? new Date(c.ends_at).getTime() : Infinity;
      if (f !== null && e < f) return false;
      if (t !== null && s > t) return false;
      return true;
    });
  };
  const drawTabs = () => {
    const count = (id: Tab) => (id === 'all' ? store.campaigns.length : store.campaigns.filter((c) => campaignStatus(c, now) === id).length);
    mount(tabsEl, TABS.map(([id, label]) => h('button', { type: 'button', class: `ghost${tab === id ? ' on' : ''}`, onclick: () => { tab = id; drawTabs(); draw(); } }, `${label} `, h('span', { class: 'chip small' }, String(count(id))))));
  };
  const draw = async () => {
    const clicks = await store.api.clicksByCampaign().catch(() => ({} as Record<string, number>));
    const order: Record<CampaignStatus, number> = { live: 0, upcoming: 1, off: 2, past: 3 };
    const rows = visible().sort((a, b) => order[campaignStatus(a, now)] - order[campaignStatus(b, now)] || b.priority - a.priority || (a.starts_at ?? '').localeCompare(b.starts_at ?? ''));
    mount(
      listEl,
      rows.length
        ? h(
          'table',
          { class: 'clist' },
          h('thead', null, h('tr', null, ['Visuel', 'Campagne', 'Statut', 'Boîtes', 'Priorité', 'Durée', 'Clics', ''].map((t) => h('th', null, t)))),
          h(
            'tbody',
            null,
            rows.map((c) => {
              const st = campaignStatus(c, now);
              const reach = reachOf(c, store, now);
              return h(
                'tr',
                { class: st },
                h('td', null, h('img', { class: 'cthumb', src: c.image_url, alt: c.alt ?? '', loading: 'lazy', width: 120, height: bannerDisplayHeight(c.image_width, c.image_height) / 5 })),
                h('td', null, h('strong', null, c.name), isPermanent(c) ? h('span', { class: 'chip small' }, 'Permanente') : null, h('small', { class: 'muted block' }, c.include_emails.length ? `${c.include_emails.length} adresse(s) ciblée(s)` : c.brand_ids.length ? c.brand_ids.map((id) => store.brands.find((b) => b.id === id)?.name ?? id).join(', ') : 'Tous les lieux')),
                h('td', null, h('span', { class: `chip ${st}` }, STATUS_LABEL[st])),
                h('td', { title: 'Boîtes pour lesquelles cette campagne est celle retenue' }, String(reach)),
                h('td', null, PRIORITY_LABELS[priorityOf(c.priority)]),
                h('td', null, durationLabel(c)),
                h('td', null, String(clicks[c.id] ?? 0)),
                h('td', { class: 'actions' }, h('button', { class: 'ghost', onclick: () => edit(c) }, 'Modifier'), h('button', {
                  class: 'ghost danger',
                  onclick: (e: Event) =>
                    busy(e.target as HTMLButtonElement, async () => {
                      if (!confirm(`Supprimer « ${c.name} » ?`)) return;
                      await store.api.deleteCampaign(c.id);
                      await reload(store);
                      refresh();
                    }),
                }, 'Supprimer')),
              );
            }),
          ),
        )
        : h('p', { class: 'muted' }, store.campaigns.length ? 'Aucune campagne ne correspond.' : 'Aucune campagne. Une bannière permanente est une campagne sans dates.'),
    );
  };
  [search, from, to].forEach((el) => el.addEventListener('input', draw));

  mount(
    root,
    h(
      'div',
      { class: 'toolbar' },
      h('h2', null, 'Campagnes'),
      h('button', { onclick: () => edit(null) }, '+ Nouvelle campagne'),
      h('span', { class: 'spacer' }),
      h('button', { class: 'ghost', disabled: true, title: 'Vue calendrier mensuelle : prévue, pas encore construite', onclick: () => (view = 'calendar') }, 'Calendrier'),
    ),
    h('p', { class: 'muted' }, `Une bannière est une campagne. ${SINGLE_CAMPAIGN_RULE} Les signatures Gmail changent à la prochaine poussée ; les signatures Apple Mail / Outlook changent d’elles-mêmes (dix minutes au plus).`),
    h('div', { class: 'cfilters' }, tabsEl, search, h('span', { class: 'ed-inline' }, from, '→', to)),
    listEl,
  );
  drawTabs();
  draw();
  void view;
  return root;
}
